import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/api.service';
import { AppComponent } from 'src/app/app.component';
import { ThemingService } from 'src/app/theming.service';
import { LambdaService } from 'src/app/lambda.service';
import { AuthService } from 'src/app/auth.service';
import { RegionBackendService } from 'src/app/region/services/regions.service';
import { Region } from 'src/app/region/models/region.interface';
import { Subscription } from 'rxjs';
import { ElementRef, ViewChild } from '@angular/core';
import {
  extractShopCompanies,
  ShopCompany,
} from 'src/app/components/shop-companies/shop-company.models';
import {
  getInvalidClientFields,
  InvalidClientField,
  markFormGroupTouched,
} from 'src/app/components/shared/client-form.helpers';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

declare var $: any;

type ClientLoginMethod = 'PASSWORD' | 'PASSWORD_OTP' | 'OTP_ONLY';

const DEFAULT_CLIENT_LOGIN_METHOD: ClientLoginMethod = 'PASSWORD';
const DEFAULT_NEW_ONLINE_LOGIN_METHOD: ClientLoginMethod = 'OTP_ONLY';

const CLIENT_LOGIN_METHOD_OPTIONS: Array<{
  labelKey: string;
  value: ClientLoginMethod;
}> = [
  { labelKey: 'CLIENT_LOGIN_PASSWORD', value: 'PASSWORD' },
  { labelKey: 'CLIENT_LOGIN_OTP_ONLY', value: 'OTP_ONLY' },
];

@Component({
  selector: 'app-edit-client',
  templateUrl: './edit-client.component.html',
  styleUrls: ['./edit-client.component.scss']
})
export class EditClientComponent implements OnInit, OnDestroy {
  @ViewChild('editClientFormContainer') editClientFormContainer?: ElementRef<HTMLElement>;
  clientId = 0;
  clientForm: FormGroup;
  loading: boolean = false;
  loadingBeneficiaries: boolean = false;
  showPassword: boolean = false;
  access: any;
  nationalIdInvalid: boolean = false;
  placeholderText: string = '';
  passwordCopied: boolean = false;
  bornDateMax: string = this.getTodayDateString();
  bornDateMaxDate: Date = new Date();
  clientSubscriptions: any[] = [{}];
  shopCompanies: ShopCompany[] = [];
  client: any;
  isClientLoaded: boolean = false;
  loginMethodOptions = CLIENT_LOGIN_METHOD_OPTIONS;
  invalidClientFields: InvalidClientField[] = [];
  originalLoginMethod: ClientLoginMethod = DEFAULT_CLIENT_LOGIN_METHOD;
  originalHasOnlineUser: boolean = false;
  passwordChangeRequested: boolean = false;
  clientBeneeficiaries: any[] = [];

  currentUser: any;

  beneficiaryForm: FormGroup;
  public provincias: Region[] = [];
  public cantones: Region[] = [];
  public distritos: Region[] = [];
  private _subscriptions:Subscription = new Subscription();
  public selectedProvinciaId: string = '';
  public selectedCantonId: string = '';
  public selectedDistritoId: string = '';
  provinciaName = "";
  cantonName = "";
  distritoName = "";
  beneficiaryRegionId: any;
  beneficiaryAddress: any;
  currentRelationship = "";
  validRelationships: string[] = ['Perro', 'Gato'];
  validateResponsibleRelationships: string[] = ['Perro', 'Gato', 'Hijo', 'Hija'];

  constructor(
    public appComponent: AppComponent,
    private apiService: ApiService,
    public themingService: ThemingService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private lambdaService: LambdaService,
    private readonly _region: RegionBackendService,
    private authService: AuthService,
  ) {
    this.clientForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      employeeId: [''],
      gender: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      documentType: ['', Validators.required],
      clientSubscription: [''],
      shopCompanyId: [null],
      loginMethod: [DEFAULT_CLIENT_LOGIN_METHOD, Validators.required],
      previousLoginMethod: [{ value: DEFAULT_CLIENT_LOGIN_METHOD, disabled: true }],
      nationalId: ['', Validators.required] ,
      isPoolRegister: [{ value: false, disabled: true }],
      username: [''],
      password: [{ value: '', disabled: true }],
      bornDate: ['', [this.bornDateValidator]],
      isVerifiedClient: [false],
      clientRegistrationDate: ['']
    });

    this.beneficiaryForm = this.fb.group({
      beneficiaryName: ['', Validators.required],
      beneficiaryNationalId: ['', Validators.required],
      beneficiaryBornDate: [''],
      beneficiaryGender: [''],
      beneficiaryPhone: [''],
      beneficiaryEmail: [''],
      beneficiaryLastName: ['', Validators.required],
      beneficiaryRelationship: [''],
      beneficiaryBreed: ['', Validators.required],
      beneficiaryColor: [''],
      beneficiaryWeight: ['', Validators.required],
      beneficiaryDocumentType: [''],
      selectedProvinciaId: [''],
      selectedCantonId: [''],
      selectedDistritoId: [''],
      beneficiaryAddress: ['']
    });

    this.route.paramMap.subscribe((params) => {
      let clientId = Number(params.get('clientId'));
      if (clientId != this.clientId) {
        this.appComponent.goToTop();
        this.clientId = clientId;
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Deshabilitar el formulario mientras se cargan los datos
    this.clientForm.disable({ emitEvent: false });
    this.currentUser = await this.authService.currentAuthUser();
    await this.getSubscriptions();
    await this.getShopCompanies();
    await this.getClient();
    
    // Habilitar el formulario solo después de cargar todos los datos
    this.clientForm.enable({ emitEvent: false });
    this.handleAccessControlChanges();
    this.isClientLoaded = true;
    
    // Registrar todas las suscripciones en _subscriptions para limpiarlas en ngOnDestroy
    this._subscriptions.add(
      this.clientForm.get('nationalId')!.valueChanges.subscribe(value => {
        this.validateNationalId(value);
      })
    );

    this.beneficiaryForm.get('nationalId')?.setValue(this.client.nationalId);
    
    this._subscriptions.add(
      this.clientForm.get('documentType')!.valueChanges.subscribe(type => {
        this.placeholderText = type === 'Passport'
          ? 'A12345678900'
          : (type === 'DIMEX'
            ? '12345678901'
            : (type === 'other'
            ? ''
            : '123456789'));
        this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
      })
    );
    
    this._subscriptions.add(
      this._region.getSubregions(this._region.crId)
      .subscribe(value => {
          this.provincias = value;
      })
    );
  }

  createBeneficiaries(): void {
    this.validateBeneficiaryNationalId();
    console.log("creando")
    if (this.nationalIdInvalid) {
      console.error('The ID number is invalid.');
      return;
    }
    console.log("this.beneficiaryForm.valid",this.beneficiaryForm.valid)
    if (this.beneficiaryForm.valid) {
      this.loadingBeneficiaries = true;
      const resourceData = this.beneficiaryForm.value;

      this.lambdaService.createBeneficiaries(
        this.themingService.shopTheming[0].shopId,
        resourceData.beneficiaryName,
        resourceData.beneficiaryNationalId,
        resourceData.beneficiaryBornDate,
        resourceData.beneficiaryGender,
        resourceData.beneficiaryPhone,
        resourceData.beneficiaryEmail,
        resourceData.nationalId,
        resourceData.beneficiaryLastName,
        resourceData.beneficiaryRelationship,
        resourceData.beneficiaryBreed,
        resourceData.beneficiaryColor,
        resourceData.beneficiaryWeight,
        resourceData.beneficiaryDocumentType,
        resourceData.selectedDistritoId,
        resourceData.beneficiaryAddress,
        this.provinciaName,
        this.cantonName,
        this.distritoName,
        this.currentUser
      )
        .then((response: any) => {
          console.log("response",response)
          const isCreated = response.data?.isCreated;
          const statusCode = response.statusCode ?? response.data?.statusCode;

          if (statusCode == 200 && isCreated == 1) {
            this.showSuccessModal();

          } else if (statusCode == 409 && isCreated == 0) {
            $('#errorTwoBeneficiariesModal').modal('show');

          } else {
            this.showGenericErrorModal();
          }
        })
        .catch((error) => {
          console.error("Error en la API:", error);
          this.showGenericErrorModal();
        })
        .finally(() => {
          this.loadingBeneficiaries = false;
        });
    }
  }

  onProvinciaChange(event: Event) {
    // const selectedId = (event.target as HTMLSelectElement).value;
    // const selectedId = this.beneficiaryForm.get('selectedProvinciaId')?.value;
    const selectElement = event.target as HTMLSelectElement;
    const selectedId = selectElement.value; // esto es el ID
    this.provinciaName = selectElement.options[selectElement.selectedIndex]?.text; // esto es el nombre
    console.log(this.provinciaName)
    this.selectedProvinciaId = selectedId;
    this.cantones = [];
    this.distritos = [];
    if (selectedId) {
      this._subscriptions.add(
        this._region.getSubregions(selectedId).subscribe((response) => {
          this.cantones = response;
        })
      );
    }
  }

  onCantonChange(event: Event) {
    // const selectedId = (event.target as HTMLSelectElement).value;
    // const selectedId = this.beneficiaryForm.get('selectedProvinciaId')?.value;
    const selectElement = event.target as HTMLSelectElement;
    const selectedId = selectElement.value; // esto es el ID
    this.cantonName = selectElement.options[selectElement.selectedIndex]?.text; // esto es el nombre
    console.log(this.cantonName)
    this.selectedCantonId = selectedId;
    this.distritos = [];
    if (selectedId) {
      this._subscriptions.add(
        this._region.getSubregions(selectedId).subscribe((response) => {
          this.distritos = response;
        })
      );
    }
  }

  onDistritoChange(event: Event) {
    // const selectedId = (event.target as HTMLSelectElement).value;
    // const selectedId = this.beneficiaryForm.get('selectedProvinciaId')?.value;
    const selectElement = event.target as HTMLSelectElement;
    const selectedId = selectElement.value; // esto es el ID
    this.distritoName = selectElement.options[selectElement.selectedIndex]?.text; // esto es el nombre
    console.log(this.distritoName)
    this.selectedDistritoId = selectedId;
    this.beneficiaryRegionId = selectedId;
  }

  onChangeRelationship(event: any) {
    this.currentRelationship = event.target.value;
  }

  validateBeneficiaryNationalId(): void {
    const value = this.beneficiaryForm.get('beneficiaryNationalId')?.value || '';
    const type = this.beneficiaryForm.get('beneficiaryDocumentType')?.value;

    if (type === 'ID') {
      this.nationalIdInvalid = !(value.length === 9 && /^\d+$/.test(value));
    } else if (type === 'Passport') {
      this.nationalIdInvalid = !/^[A-Z][0-9]{8,11}$/.test(value);
    } else if (type === 'DIMEX') {
      this.nationalIdInvalid = !/^[1-9][0-9]{11}$/.test(value);
    } else if (type === 'other') {
      this.nationalIdInvalid = value.length === 0; // Inválido si está vacío
    } else {
      this.nationalIdInvalid = false;
    }
  }

  updateBeneficiaryNationalIdValidators(relationship: string): void {
    if (this.validRelationships.includes(relationship)) {
      // Si el valor está en el arreglo, quitar la validación
      this.beneficiaryForm.get('beneficiaryNationalId')?.clearValidators();
      this.beneficiaryForm.get('beneficiaryLastName')?.clearValidators();
    } else {
      // Si no, agregar la validación requerida
      this.beneficiaryForm.get('beneficiaryNationalId')?.setValidators(Validators.required);
      this.beneficiaryForm.get('beneficiaryLastName')?.setValidators(Validators.required);
    }
    // Actualizar el estado del control
    this.beneficiaryForm.get('beneficiaryNationalId')?.updateValueAndValidity();
    this.beneficiaryForm.get('beneficiaryLastName')?.updateValueAndValidity();
  }

  showGenericErrorModal(): void {
    $('#errorBeneficiariesModal').modal('show');
  }

  showSuccessModal(): void {
    $('#successBeneficiariesModal').modal('show');
    $('#successBeneficiariesModal').on('hidden.bs.modal', () => {
      this.beneficiaryForm.reset();
      //this.creationBeneficiarySuccess.emit();
    });
  }

  showDuplicateErrorModal(errorMessage: string): void {
    $('#errorTwoBeneficiariesModal .modal-body').text(errorMessage);
    $('#errorTwoBeneficiariesModal').modal('show');
    $('#errorTwoBeneficiariesModal').on('hidden.bs.modal', () => {

    });
  }

  closeBeneficiariesModal() {
    $('#successBeneficiariesModal').modal('hide');
    $('#errorBeneficiariesModal').modal('hide');
    $('#requiredBeneficiariesModal').modal('hide');
    $('#errorTwoBeneficiariesModal').modal('hide');
  }

  async getClient() {
    this.loading = true;
    try {
      const data: any = await this.lambdaService.getClient(this.themingService.shopId, this.clientId);
      this.client = data.client;
      this.clientBeneeficiaries = data.beneficiaries || [];
      const username = data.client.username || '';
      this.originalHasOnlineUser = username.trim() !== '';
      this.originalLoginMethod = this.normalizeLoginMethod(
        this.client.loginMethod,
        this.originalHasOnlineUser ? DEFAULT_CLIENT_LOGIN_METHOD : DEFAULT_NEW_ONLINE_LOGIN_METHOD
      );
      
      // Mapea los datos correctamente al FormGroup
      const formData = {
        ...data.client,  // Copia todas las propiedades existentes
        clientSubscription: this.client.clientSubscriptionId || '',  // Mapea clientSubscriptionId al FormControl clientSubscription
        shopCompanyId: this.normalizeShopCompanyId(
          this.client.shopCompanyId
        ),
        loginMethod: this.originalLoginMethod,
        previousLoginMethod: this.originalLoginMethod,
        bornDate: this.parseDateForDatepicker(
          this.client.bornDate || this.client.birthDate
        ),
        isPoolRegister: this.originalHasOnlineUser
          ? true
          : data.client.isPoolRegister === true || data.client.isPoolRegister === 1,
        clientRegistrationDate: this.addSixHours(this.client.registrationDate)
      };
      
      // Usar patchValue en lugar de setValue para evitar errores de validación
      this.clientForm.patchValue(formData, { emitEvent: false });

    } catch (error) {
      console.log(error);
      this.isClientLoaded = false;
    }
    this.loading = false;
  }

  addSixHours(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    date.setHours(date.getHours() + 6);
    return date.toISOString();
  }

  handleAccessControlChanges(): void {
    this._subscriptions.add(
      this.clientForm.get('isPoolRegister')!.valueChanges.subscribe(() => {
        this.updateAccessControls();
      })
    );

    this._subscriptions.add(
      this.clientForm.get('loginMethod')!.valueChanges.subscribe(() => {
        this.updateAccessControls();
      })
    );

    this.updateAccessControls();
  }

  hasOnlineUser(): boolean {
    return this.originalHasOnlineUser;
  }

  isOnlinePurchasesEnabled(): boolean {
    return this.hasOnlineUser() || this.clientForm.get('isPoolRegister')?.value === true;
  }

  isPasswordLoginSelected(): boolean {
    return this.normalizeLoginMethod(
      this.clientForm.get('loginMethod')?.value,
      this.hasOnlineUser() ? DEFAULT_CLIENT_LOGIN_METHOD : DEFAULT_NEW_ONLINE_LOGIN_METHOD
    ) === 'PASSWORD';
  }

  shouldShowLoginMethod(): boolean {
    return this.hasOnlineUser() || this.isOnlinePurchasesEnabled();
  }

  shouldShowPasswordField(): boolean {
    return this.isPasswordLoginSelected() && (
      (!this.originalHasOnlineUser && this.isOnlinePurchasesEnabled()) ||
      (this.originalLoginMethod === 'OTP_ONLY') ||
      this.passwordChangeRequested
    );
  }

  shouldShowChangePasswordAction(): boolean {
    return this.hasOnlineUser()
      && this.isPasswordLoginSelected()
      && this.originalLoginMethod === 'PASSWORD'
      && !this.passwordChangeRequested;
  }

  startPasswordChange(): void {
    this.passwordChangeRequested = true;
    this.updateAccessControls();
  }

  cancelPasswordChange(): void {
    this.passwordChangeRequested = false;
    this.clientForm.get('password')?.reset('', { emitEvent: false });
    this.passwordCopied = false;
    this.showPassword = false;
    this.updateAccessControls();
  }

  updateAccessControls(): void {
    const isPoolRegisterControl = this.clientForm.get('isPoolRegister');
    const loginMethodControl = this.clientForm.get('loginMethod');
    const passwordControl = this.clientForm.get('password');

    if (!isPoolRegisterControl || !loginMethodControl || !passwordControl) {
      return;
    }

    if (this.hasOnlineUser()) {
      isPoolRegisterControl.setValue(true, { emitEvent: false });
      isPoolRegisterControl.disable({ emitEvent: false });
    } else {
      isPoolRegisterControl.enable({ emitEvent: false });
    }

    if (!this.isOnlinePurchasesEnabled()) {
      loginMethodControl.disable({ emitEvent: false });
      loginMethodControl.setValue(DEFAULT_NEW_ONLINE_LOGIN_METHOD, { emitEvent: false });

      passwordControl.disable({ emitEvent: false });
      passwordControl.clearValidators();
      passwordControl.reset('', { emitEvent: false });
      passwordControl.updateValueAndValidity({ emitEvent: false });

      this.passwordChangeRequested = false;
      this.showPassword = false;
      this.passwordCopied = false;
      return;
    }

    loginMethodControl.enable({ emitEvent: false });

    const fallbackLoginMethod = this.hasOnlineUser()
      ? DEFAULT_CLIENT_LOGIN_METHOD
      : DEFAULT_NEW_ONLINE_LOGIN_METHOD;
    const loginMethod = this.normalizeLoginMethod(
      loginMethodControl.value,
      fallbackLoginMethod
    );

    if (loginMethodControl.value !== loginMethod) {
      loginMethodControl.setValue(loginMethod, { emitEvent: false });
    }

    if (loginMethod === 'PASSWORD' && this.shouldShowPasswordField()) {
      passwordControl.enable({ emitEvent: false });
      passwordControl.setValidators([Validators.required, this.passwordValidator]);
    } else {
      passwordControl.disable({ emitEvent: false });
      passwordControl.clearValidators();
      passwordControl.reset('', { emitEvent: false });

      if (loginMethod !== 'PASSWORD') {
        this.passwordChangeRequested = false;
      }

      this.showPassword = false;
      this.passwordCopied = false;
    }

    passwordControl.updateValueAndValidity({ emitEvent: false });
  }

  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*]/.test(password);
    const isValidLength = password.length >= 8;

    return !(hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isValidLength)
      ? { passwordStrength: { hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, isValidLength } }
      : null;
  }

  bornDateValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const selectedDate = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(selectedDate.getTime())) {
      return { invalidDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    return selectedDate > today ? { futureDate: true } : null;
  }

  generatePassword(): void {
    if (this.clientForm.get('password')?.enabled) {
      const length = 12;
      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const lower = 'abcdefghijklmnopqrstuvwxyz';
      const numbers = '0123456789';
      const specials = '!@#$%^&*';
      const allChars = `${upper}${lower}${numbers}${specials}`;

      const basePassword = [
        upper.charAt(Math.floor(Math.random() * upper.length)),
        lower.charAt(Math.floor(Math.random() * lower.length)),
        numbers.charAt(Math.floor(Math.random() * numbers.length)),
        specials.charAt(Math.floor(Math.random() * specials.length)),
      ];

      for (let i = basePassword.length; i < length; i++) {
        basePassword.push(allChars.charAt(Math.floor(Math.random() * allChars.length)));
      }

      const password = basePassword
        .sort(() => Math.random() - 0.5)
        .join('');

      this.clientForm.controls['password'].setValue(password);
      this.passwordCopied = false;
    }
  }

  togglePasswordVisibility(): void {
    if (this.clientForm.get('password')?.enabled) {
      this.showPassword = !this.showPassword;
    }
  }

  onDocumentTypeChange(event: Event): void {
      const documentType = (event.target as HTMLSelectElement).value;
      this.clientForm.get('documentType')?.setValue(documentType);
      this.validateNationalId(this.clientForm.get('nationalId')?.value || '');
    }

    validateNationalId(value: string): void {
      value = value.trim();
      const documentType = this.clientForm.get('documentType')?.value;
      const nationalIdControl = this.clientForm.get('nationalId');
      if (!nationalIdControl) {
        return;
      }

      if (!value) {
        this.nationalIdInvalid = false;
        this.removeCustomError(nationalIdControl, 'invalid');
        return;
      }

      if (documentType === 'ID') {
        const isNumeric = /^\d+$/.test(value);
        const isValidLength = value.length === 9;
        this.nationalIdInvalid = !isNumeric || !isValidLength;
      } else if (documentType === 'Passport') {
        this.nationalIdInvalid = !/^[A-Z][0-9]{8,11}$/.test(value);
      } else if (documentType === 'DIMEX') {
      this.nationalIdInvalid = !/^[1-9][0-9]{11}$/.test(value);
      } else {
        this.nationalIdInvalid = false;
      }

      if (this.nationalIdInvalid) {
        nationalIdControl.setErrors({ ...(nationalIdControl.errors || {}), invalid: true });
      } else {
        this.removeCustomError(nationalIdControl, 'invalid');
      }
    }

  async getSubscriptions() {
    this.loading = true;
    try {
      let response: any = await this.lambdaService.getClientSubscriptions(this.themingService.shopId);
      this.clientSubscriptions = response.subscriptions;
      this.loading = false;
    } catch (error) {
      console.error(error);
      this.loading = false;
    }
  }

  async getShopCompanies() {
    try {
      const response: any = await this.lambdaService.getShopCompanies(
        this.themingService.shopId,
        true
      );
      this.shopCompanies = extractShopCompanies(response).filter(
        (company) => company.status === 1
      );
    } catch (error) {
      console.error('Error loading shop companies:', error);
      this.shopCompanies = [];
    }
  }

  formatDateToYMD(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  private parseDateForDatepicker(value: string | Date | null | undefined): Date | string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? '' : value;
    }

    const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date;
  }

  private formatDateForPayload(value: unknown): string | undefined {
    if (!value) {
      return undefined;
    }

    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeShopCompanyId(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const id = Number(value);
    return Number.isFinite(id) ? id : null;
  }

  private normalizeLoginMethod(
    value: unknown,
    fallback: ClientLoginMethod = DEFAULT_CLIENT_LOGIN_METHOD
  ): ClientLoginMethod {
    return this.loginMethodOptions.some((option) => option.value === value)
      ? (value as ClientLoginMethod)
      : fallback;
  }

  private normalizePhoneValue(value: unknown): string {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const phoneNumber =
      parsePhoneNumberFromString(value) ||
      parsePhoneNumberFromString(value, 'CR');

    return phoneNumber?.number || value;
  }

  updateClient(): void {
    if (!this.validateClientFormBeforeSubmit()) {
      return;
    }

    this.loading = true;
    const clientData = this.clientForm.getRawValue();
    clientData.isVerifiedClient = clientData.isVerifiedClient ? 1 : 0;
    const shopCompanyId = this.normalizeShopCompanyId(
      clientData.shopCompanyId
    );
    const loginMethod = this.normalizeLoginMethod(
      clientData.loginMethod,
      this.hasOnlineUser() ? DEFAULT_CLIENT_LOGIN_METHOD : DEFAULT_NEW_ONLINE_LOGIN_METHOD
    );
    const isPoolRegister = this.hasOnlineUser()
      ? true
      : clientData.isPoolRegister === true;
    const password = this.shouldShowPasswordField() && loginMethod === 'PASSWORD'
      ? clientData.password || ''
      : '';
    const previousLoginMethod = this.normalizeLoginMethod(
      clientData.previousLoginMethod,
      this.originalLoginMethod
    );
    const username = clientData.username || this.client?.username || '';
    // console.log(
    //   this.themingService.shopId,
    //   this.clientId,
    //   clientData.username,
    //   clientData.firstName,
    //   clientData.lastName,
    //   clientData.email,
    //   clientData.phone,
    //   clientData.nationalId,
    //   clientData.isPoolRegister,
    //   clientData.gender,
    //   clientData.password,
    //   this.access = clientData.password,
    //   clientData.isVerifiedClient,
    //   clientData.documentType,
    //   clientData.clientSubscription
    // )
    this.lambdaService.updateClient(
      this.themingService.shopId,
      this.clientId,
      username,
      clientData.firstName,
      clientData.lastName,
      clientData.email,
      this.normalizePhoneValue(clientData.phone),
      clientData.nationalId,
      clientData.employeeId,
      isPoolRegister,
      clientData.gender,
      password,
      this.access = password,
      clientData.isVerifiedClient,
      clientData.documentType,
      clientData.clientSubscription,
      this.formatDateToYMD(clientData.clientRegistrationDate),
      shopCompanyId,
      loginMethod,
      previousLoginMethod,
      this.formatDateForPayload(clientData.bornDate)
    ).then((data: any) => {
      this.loading = false;
      if (data.statusCode === 200) {
        $('#successEditClientModal').modal('show');
        $('#successEditClientModal').on('hidden.bs.modal', () => {
          this.router.navigate(['/clients']);
        });
      } else {
        $('#errorEditClientModal').modal('show');
      }
    }).catch((error) => {
      console.log(error);
      $('#errorEditClientModal').modal('show');
      this.loading = false;
    });
  }

  async copyPassword(): Promise<void> {
    const password = this.clientForm.get('password')?.value;
    if (!password) {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      } else {
        const helperInput = document.createElement('textarea');
        helperInput.value = password;
        helperInput.style.position = 'fixed';
        helperInput.style.left = '-9999px';
        document.body.appendChild(helperInput);
        helperInput.select();
        document.execCommand('copy');
        document.body.removeChild(helperInput);
      }
      this.passwordCopied = true;
      setTimeout(() => {
        this.passwordCopied = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  }

  private validateClientFormBeforeSubmit(): boolean {
    markFormGroupTouched(this.clientForm);
    this.invalidClientFields = getInvalidClientFields(this.clientForm);

    if (this.clientForm.invalid) {
      $('#requiredEditClientModal').modal('show');
      this.focusFirstInvalidControl();
      return false;
    }

    this.invalidClientFields = [];
    return true;
  }

  private focusFirstInvalidControl(): void {
    const firstInvalidControlName = Object.keys(this.clientForm.controls).find(
      (controlName) => this.clientForm.get(controlName)?.invalid
    );

    if (!firstInvalidControlName) {
      return;
    }

    const hostElement = this.editClientFormContainer?.nativeElement || document;
    const invalidElement = hostElement.querySelector(
      `[formControlName="${firstInvalidControlName}"]`
    ) as HTMLElement | null;

    if (invalidElement) {
      invalidElement.focus();
      invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private removeCustomError(control: AbstractControl, errorKey: string): void {
    const errors = control.errors;
    if (!errors || !errors[errorKey]) {
      return;
    }

    const { [errorKey]: _, ...remainingErrors } = errors;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private getTodayDateString(): string {
    return new Date().toISOString().split('T')[0];
  }

  closeModal() {
    $('#successEditClientModal').modal('hide');
    $('#errorEditClientModal').modal('hide');
    $('#requiredEditClientModal').modal('hide');
  }

  ngOnDestroy(): void {
    // Limpiar todas las suscripciones activas
    this._subscriptions.unsubscribe();
  }
}
